export const compressImage = (dataURL: string, maxWidth = 1200, quality = 0.7): Promise<string> => {
  console.log('[compressImage] Attempting to compress. Input Datalength:', dataURL.length);
  return new Promise((resolve, reject) => {
    if (!dataURL || !dataURL.startsWith('data:image')) {
      console.error('[compressImage] Invalid DataURL input.');
      return reject(new Error('Invalid DataURL input for compression.'));
    }
    const img = new Image();
    img.onload = () => {
      console.log('[compressImage] Image loaded. Original dims:', img.width, 'x', img.height);
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        const ratio = maxWidth / width;
        width = maxWidth;
        height = height * ratio;
        console.log('[compressImage] Resized dims:', width, 'x', height);
      } else {
        console.log('[compressImage] No resize needed.');
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        try {
          ctx.drawImage(img, 0, 0, width, height);
          const compressedDataURL = canvas.toDataURL('image/jpeg', quality);
          console.log('[compressImage] Compression successful. Output Datalength:', compressedDataURL.length);
          resolve(compressedDataURL);
        } catch (e) {
          console.error('[compressImage] Error during canvas.toDataURL:', e);
          reject(e);
        }
      } else {
        console.error('[compressImage] Failed to get canvas context.');
        reject(new Error('Failed to get canvas context for image compression.'));
      }
    };

    img.onerror = (errEvent) => {
      console.error('[compressImage] img.onerror triggered:', errEvent);
      reject(new Error('Image loading failed for compression.'));
    };

    try {
      img.src = dataURL;
      console.log('[compressImage] img.src set.');
    } catch (e) {
      console.error('[compressImage] Error setting img.src:', e);
      reject(e);
    }
  });
};

export const calculateImageSize = (dataURL: string): string => {
  try {
    // Handle empty or invalid input
    if (!dataURL || typeof dataURL !== 'string') {
      return '';
    }

    // Check if it's a base64 data URL
    if (dataURL.startsWith('data:image')) {
  // Remove the data URL prefix to get just the base64 data
  const base64 = dataURL.split(',')[1];
      
      if (!base64) {
        return '';
      }

  // Calculate size in bytes (base64 is 4/3 the size of the actual data)
  const sizeInBytes = Math.ceil((base64.length * 3) / 4);

  // Convert to KB or MB for display
  if (sizeInBytes > 1024 * 1024) {
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  } else {
    return `${Math.ceil(sizeInBytes / 1024)} KB`;
      }
    } 
    // Handle HTTP/HTTPS URLs (from Supabase Storage, etc.)
    else if (dataURL.startsWith('http')) {
      // For HTTP URLs, we can't easily calculate size without downloading
      // Return a generic indicator that it's an uploaded image
      return 'Uploaded';
    }
    // Handle any other format
    else {
      return '';
    }
  } catch (error) {
    console.warn('[calculateImageSize] Error calculating image size:', error);
    return '';
  }
}; 

// New function for calculating image dimensions with aspect ratio preservation
interface ImageDimensions {
  width: number;
  height: number;
}

interface ImageSizeOptions {
  maxWidth?: number;
  maxHeight?: number;
  width: number;
  height: number;
}

export const calculateImageDimensions = (options: ImageSizeOptions): ImageDimensions => {
  const { width, height, maxWidth = 120, maxHeight = 120 } = options;
  
  // If both dimensions are already smaller than max, return as is
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }
  
  // Calculate aspect ratio
  const aspectRatio = width / height;
  
  // Determine which dimension to constrain
  if (width / maxWidth > height / maxHeight) {
    // Width is the limiting factor
    return {
      width: maxWidth,
      height: Math.round(maxWidth / aspectRatio)
    };
  } else {
    // Height is the limiting factor
    return {
      width: Math.round(maxHeight * aspectRatio),
      height: maxHeight
    };
  }
}; 